import { hashKey } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import { useInfiniteBroadcastClips } from '@/features/clips/api/get-broadcast-clips';
import { clipsQueryKeys } from '@/features/clips/api/query-keys';
import { useInfiniteSegmentClips } from '@/features/segments/api/get-segment-clips-by-id';
import { ProgramBroadcastFiltersState } from '@/stores/program-broadcast-filters';

import { useBroadcastScheduleProgram } from '../context/BroadcastScheduleProgramContext';

type CutsListSourceParams = {
  /** Which endpoint lists the cuts: the programme's, or the active segment's. */
  kind: 'program' | 'segment';
  id?: string;
  filters?: Partial<ProgramBroadcastFiltersState>;
};

/**
 * Runs the cut list for one review surface and publishes it to the context the panel reads.
 *
 * Both surfaces share this so the accumulated list is built once: the flatten has to be memoised
 * on the query data, and an unmemoised one sets state on every render.
 */
export function useCutsListSource({ kind, id = '', filters }: CutsListSourceParams) {
  const {
    setClipBySegmentItems,
    setTotalClipsBySegment,
    setIsLoadingClipsBySegment,
    setClipsPaging,
    setCutsListSource,
  } = useBroadcastScheduleProgram();

  const programQuery = useInfiniteBroadcastClips({
    id: kind === 'program' ? id : '',
    filters,
  });
  const segmentQuery = useInfiniteSegmentClips({
    id: kind === 'segment' ? id : '',
    filters,
  });

  const query = kind === 'program' ? programQuery : segmentQuery;
  const { data, isLoading, isPlaceholderData, hasNextPage, isFetchingNextPage, fetchNextPage } =
    query;

  const items = useMemo(() => data?.pages.flatMap(page => page.data) ?? [], [data]);
  // The freshest page carries the freshest count.
  const total = data?.pages.at(-1)?.total ?? 0;

  /**
   * Changes exactly when the query restarts, which is when the loaded list is discarded.
   * Hashed the way React Query hashes its own keys, so the ordering of the filter fields and
   * their undefined values are handled the same way here as they are there.
   */
  const listKey = useMemo(
    () =>
      hashKey(
        kind === 'program'
          ? clipsQueryKeys.infiniteByProgram(id, filters)
          : clipsQueryKeys.infiniteBySegment(id, filters)
      ),
    [kind, id, filters]
  );

  const clipsPaging = useMemo(
    () => ({
      // While the previous result is still on screen, the rows belong to the old query: asking
      // for its next batch would fetch page two of a list whose page one has not arrived.
      hasNextPage: !!hasNextPage && !isPlaceholderData,
      isFetchingNextPage,
      fetchNextPage: () => {
        fetchNextPage();
      },
      listKey,
      isRestarting: isPlaceholderData,
    }),
    [hasNextPage, isPlaceholderData, isFetchingNextPage, fetchNextPage, listKey]
  );

  useEffect(() => {
    setClipBySegmentItems(items);
    setTotalClipsBySegment(total);
  }, [items, total, setClipBySegmentItems, setTotalClipsBySegment]);

  // Only a first load blanks the panel. A refetch — every clip mutation invalidates `['clips']` —
  // keeps the rows on screen instead of emptying the whole panel while the pages come back.
  useEffect(() => {
    setIsLoadingClipsBySegment(isLoading);
  }, [isLoading, setIsLoadingClipsBySegment]);

  useEffect(() => {
    setClipsPaging(clipsPaging);
  }, [clipsPaging, setClipsPaging]);

  useEffect(() => {
    setCutsListSource(id ? { kind, id } : null);
    return () => setCutsListSource(null);
  }, [kind, id, setCutsListSource]);
}
